import { Colors, Heading, Icon, Pagination } from '@amelio/ui-library';

import { MeetingCard } from './MeetingCard';
import { CARD_STACK_GAP, SECTION_TITLE } from './meetingStyles';

import type { MeetingPlanTitles } from '@/pages/ThematicMeetings/utils/meetingTitle';
import type { ThematicMeetingListItem } from '@/types/thematic-meetings';
import type { IconProps } from '@amelio/ui-library';

import { EmptyState } from '@/components/EmptyState/EmptyState';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { MEETINGS_PAGE_SIZE } from '@/pages/ThematicMeetings/constants';
import { getMeetingTitle } from '@/pages/ThematicMeetings/utils/meetingTitle';
import { lastPageOf } from '@/utils/pagination';

interface MeetingListProps {
    title: string;
    /** Icon shown in front of the section title (clock for upcoming, clipboard for previous). */
    iconName?: IconProps['name'];
    meetings: ThematicMeetingListItem[];
    planTitles: MeetingPlanTitles;
    isLoading: boolean;
    /** Whether the list draws its own spinner while it loads. A page loading two lists at once
     * shows one spinner, not two. @default true */
    showsLoader?: boolean;
    /** The meetings could not be read: the list says so instead of calling itself empty. */
    isError: boolean;
    /** Whether the meeting-template catalog itself is still loading: a card whose topic is not
     * (yet) in `planTitles` only shows a skeleton while this is true. Once the catalog
     * has loaded, a topic still missing from it is a genuinely unknown topic, not a pending one,
     * and gets a fallback label instead of an indefinite skeleton. */
    isTemplatesLoading: boolean;
    emptyMessage: string;
    currentPage: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onView: (meeting: ThematicMeetingListItem) => void;
    onMarkCompleted: (meeting: ThematicMeetingListItem) => void;
    onReopen: (meeting: ThematicMeetingListItem) => void;
    onDelete: (meeting: ThematicMeetingListItem) => void;
    dataTestId: string;
}

/** Stands in for a card whose template title has not resolved yet (the catalog is still loading),
 * so the list never shows a card with a blank title. */
const MeetingCardSkeleton = ({ dataTestId }: { dataTestId: string }) => (
    <div data-testid={dataTestId}>
        <Skeleton className={'flex items-center gap-4 rounded-lg bg-white p-4 shadow-sm'}>
            <Bone
                color={Colors.BASE_100}
                className={'h-9 w-9 shrink-0 rounded-full'}
            />

            <div className={'flex min-w-0 flex-1 flex-col gap-2'}>
                <Bone
                    color={Colors.BASE_100}
                    className={'h-3.5 w-40'}
                />
                <Bone
                    color={Colors.BASE_100}
                    className={'h-3 w-56'}
                />
            </div>

            <Bone
                color={Colors.BASE_100}
                className={'h-6 w-20 shrink-0 rounded-full'}
            />
        </Skeleton>
    </div>
);

export const MeetingList = ({
    title,
    iconName,
    meetings,
    planTitles,
    isLoading,
    showsLoader = true,
    isError,
    isTemplatesLoading,
    emptyMessage,
    currentPage,
    totalCount,
    onPageChange,
    onView,
    onMarkCompleted,
    onReopen,
    onDelete,
    dataTestId,
}: MeetingListProps) => {
    const { t } = useAmelioTranslations();
    const totalPages = lastPageOf(totalCount, MEETINGS_PAGE_SIZE);

    const showsMessage = isError || (!isLoading && meetings.length === 0);
    const message = isError ? t('thematicMeetings.list.loadError') : emptyMessage;

    return (
        <div
            className={`flex flex-col ${CARD_STACK_GAP}`}
            data-testid={dataTestId}
        >
            <div className={'flex items-center gap-2'}>
                {/* `lg` is the library's 24px step. Navy, as in the design: the icon belongs to the
                    section title it precedes, so it takes a heading colour rather than the grey of
                    secondary text. */}
                {iconName && (
                    <Icon
                        name={iconName}
                        size={'lg'}
                        color={Colors.NAVY}
                        ariaHidden
                    />
                )}

                <Heading
                    level={4}
                    weight={'regular'}
                    className={SECTION_TITLE}
                >
                    {title}
                </Heading>
            </div>

            <OverlayLoader isLoading={isLoading && showsLoader}>
                {showsMessage ? (
                    <EmptyState
                        message={message}
                        className={'py-8'}
                    />
                ) : (
                    <div className={`flex flex-col ${CARD_STACK_GAP}`}>
                        {meetings.map((meeting) => {
                            const meetingTitle = getMeetingTitle(meeting, planTitles);

                            // A missing title only means "still loading" while the catalog itself
                            // is: once it has loaded, a topic it doesn't contain is genuinely
                            // unknown, so the card renders with a fallback label instead of
                            // showing a skeleton forever.
                            if (meetingTitle === undefined && isTemplatesLoading) {
                                return (
                                    <MeetingCardSkeleton
                                        key={meeting.id}
                                        dataTestId={`meeting-card-skeleton-${meeting.id}`}
                                    />
                                );
                            }

                            return (
                                <MeetingCard
                                    key={meeting.id}
                                    meeting={meeting}
                                    title={meetingTitle ?? t('thematicMeetings.list.unknownTopic')}
                                    onView={() => onView(meeting)}
                                    onMarkCompleted={() => onMarkCompleted(meeting)}
                                    onReopen={() => onReopen(meeting)}
                                    onDelete={() => onDelete(meeting)}
                                />
                            );
                        })}
                    </div>
                )}
            </OverlayLoader>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={MEETINGS_PAGE_SIZE}
                onPageChange={onPageChange}
                isLoading={isLoading}
                variant={'rounded'}
                dataTestId={`${dataTestId}-pagination`}
            />
        </div>
    );
};
